import { addDays, format, isValid, parseISO, startOfDay } from 'date-fns';

/** Parâmetros de estadia exigidos pelo Booking para expor allRoomPhotos (tags por quarto). */
export interface BookingStayParams {
  checkin: string;
  checkout: string;
  groupAdults: number;
  groupChildren: number;
  noRooms: number;
  room1: string;
}

export type BookingStayParamsInput = Partial<BookingStayParams> & {
  checkin?: string;
  checkout?: string;
};

const DEFAULT_STAY: BookingStayParams = {
  checkin: '',
  checkout: '',
  groupAdults: 2,
  groupChildren: 0,
  noRooms: 1,
  room1: 'A,A',
};

export function isBookingHotelUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return (
      parsed.hostname.includes('booking.com') &&
      parsed.pathname.includes('/hotel/')
    );
  } catch {
    return false;
  }
}

function isFutureStay(checkin: string, checkout: string): boolean {
  const today = startOfDay(new Date());
  const checkInDate = parseISO(checkin);
  const checkOutDate = parseISO(checkout);
  return (
    isValid(checkInDate) &&
    isValid(checkOutDate) &&
    checkInDate >= today &&
    checkOutDate > checkInDate
  );
}

/** Datas futuras padrão (+30 / +37 dias) quando a hospedagem não tem datas válidas. */
export function getDefaultBookingStayParams(): BookingStayParams {
  const checkInDate = addDays(startOfDay(new Date()), 30);
  const checkOutDate = addDays(checkInDate, 7);
  return {
    ...DEFAULT_STAY,
    checkin: format(checkInDate, 'yyyy-MM-dd'),
    checkout: format(checkOutDate, 'yyyy-MM-dd'),
  };
}

/**
 * Resolve parâmetros de estadia: usa as datas informadas se forem futuras;
 * caso contrário, aplica o padrão (+30 / +7 dias).
 */
export function resolveBookingStayParams(
  input?: BookingStayParamsInput | null
): BookingStayParams {
  const checkin = input?.checkin?.trim();
  const checkout = input?.checkout?.trim();

  if (checkin && checkout && isFutureStay(checkin, checkout)) {
    return {
      checkin,
      checkout,
      groupAdults: input?.groupAdults ?? DEFAULT_STAY.groupAdults,
      groupChildren: input?.groupChildren ?? DEFAULT_STAY.groupChildren,
      noRooms: input?.noRooms ?? DEFAULT_STAY.noRooms,
      room1: input?.room1 ?? DEFAULT_STAY.room1,
    };
  }

  const defaults = getDefaultBookingStayParams();
  return {
    ...defaults,
    groupAdults: input?.groupAdults ?? defaults.groupAdults,
    groupChildren: input?.groupChildren ?? defaults.groupChildren,
    noRooms: input?.noRooms ?? defaults.noRooms,
    room1: input?.room1 ?? defaults.room1,
  };
}

/** Injeta check-in, check-out e ocupação na URL do hotel (preserva path/locale). */
export function appendBookingStayParams(
  url: string,
  params: BookingStayParams
): string {
  const parsed = new URL(url);

  parsed.searchParams.set('checkin', params.checkin);
  parsed.searchParams.set('checkout', params.checkout);
  parsed.searchParams.set('group_adults', String(params.groupAdults));
  parsed.searchParams.set('group_children', String(params.groupChildren));
  parsed.searchParams.set('no_rooms', String(params.noRooms));
  parsed.searchParams.set('room1', params.room1);

  return parsed.toString();
}

/**
 * Prepara URL do Booking para /scrape-images (tags de quarto em allRoomPhotos).
 * URLs que não são de hotel Booking são retornadas sem alteração.
 */
export function prepareBookingUrlForScrape(
  url: string,
  stayInput?: BookingStayParamsInput | null
): string {
  if (!isBookingHotelUrl(url)) {
    return url;
  }

  const params = resolveBookingStayParams(stayInput);
  return appendBookingStayParams(url, params);
}

/** URL canônica do hotel (sem query de datas/ocupação). */
export function getBookingHotelBaseUrl(url: string): string {
  const parsed = new URL(url);
  parsed.search = '';
  return parsed.toString();
}

/**
 * Datas alternativas para enriquecer tags quando o Booking não expõe allRoomPhotos
 * nas datas da busca (ex.: sem disponibilidade na tabela de quartos).
 * +90 / +97 dias costuma expor o mapeamento foto→quarto em hotéis urbanos.
 */
export function getBookingStayParamsForTagEnrichment(): BookingStayParams {
  const checkInDate = addDays(startOfDay(new Date()), 90);
  const checkOutDate = addDays(checkInDate, 7);
  return {
    ...DEFAULT_STAY,
    checkin: format(checkInDate, 'yyyy-MM-dd'),
    checkout: format(checkOutDate, 'yyyy-MM-dd'),
  };
}

export function extractBstaticImageId(imageUrl: string): string | null {
  const match = imageUrl.match(/\/(\d+)\.(?:jpe?g|webp|png)(?:\?|$)/i);
  return match ? match[1] : null;
}

export interface TaggedImageLike {
  url: string;
  tag?: string | null;
}

/** Aplica tags de uma segunda extração (outras datas) nas imagens já obtidas, pelo ID bstatic. */
export function mergeRoomTagsByImageId<T extends TaggedImageLike>(
  primary: T[],
  tagSource: TaggedImageLike[]
): { images: T[]; mergedCount: number } {
  const tagByImageId = new Map<string, string>();

  for (const item of tagSource) {
    const imageId = extractBstaticImageId(item.url);
    const tag = item.tag?.trim();
    if (imageId && tag) {
      tagByImageId.set(imageId, tag);
    }
  }

  let mergedCount = 0;
  const images = primary.map((item) => {
    if (item.tag?.trim()) {
      return item;
    }
    const imageId = extractBstaticImageId(item.url);
    if (!imageId) {
      return item;
    }
    const tag = tagByImageId.get(imageId);
    if (!tag) {
      return item;
    }
    mergedCount += 1;
    return { ...item, tag };
  });

  return { images, mergedCount };
}

export function formatStayDateRange(params: BookingStayParams): string {
  return `${params.checkin} – ${params.checkout}`;
}
